/**
 * Iconic Product Slider Component
 * Smooth swipe, drag scrolling, arrow navigation, and Shopify Theme Customizer lifecycle support.
 */

class IconicProductSlider {
  constructor(container) {
    this.container = container;
    this.viewport = container.querySelector('[data-slider-viewport]');
    this.track = container.querySelector('[data-slider-track]');
    this.prevBtn = container.querySelector('[data-slider-prev]');
    this.nextBtn = container.querySelector('[data-slider-next]');

    if (!this.viewport || !this.track) return;

    // Drag State
    this.isDown = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.hasMoved = false;
    this.moveDistance = 0;

    this.init();
  }

  init() {
    this.bindNavigation();
    this.bindDragScroll();
    this.updateArrowStates();

    // Resize observer
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateArrowStates();
      });
      this.resizeObserver.observe(this.viewport);
    }

    // Scroll listener
    this.onScroll = () => this.updateArrowStates();
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
  }

  bindNavigation() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollByCard(-1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollByCard(1);
      });
    }
  }

  scrollByCard(direction) {
    const card = this.track.querySelector('.product-card, .iconic-product-card');
    if (!card) return;

    const cardWidth = card.getBoundingClientRect().width;
    const gap = parseFloat(window.getComputedStyle(this.track).gap) || 16;
    const scrollAmount = (cardWidth + gap) * direction;

    try {
      this.viewport.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    } catch (err) {
      this.viewport.scrollLeft += scrollAmount;
    }
  }

  bindDragScroll() {
    const vp = this.viewport;

    // Mouse Dragging (Desktop)
    const handleMouseDown = (e) => {
      // Don't drag if clicking buttons, quick view triggers, or form inputs
      if (e.target.closest('button, input, select, textarea, .product-card__atc, .product-card__quickview, .iconic-floating-btn, .iconic-quick-view-icon-btn')) {
        return;
      }

      this.isDown = true;
      this.hasMoved = false;
      this.moveDistance = 0;
      this.startX = e.pageX - vp.offsetLeft;
      this.scrollLeft = vp.scrollLeft;
      vp.classList.add('is-dragging');
    };

    const handleMouseMove = (e) => {
      if (!this.isDown) return;
      e.preventDefault();
      const x = e.pageX - vp.offsetLeft;
      const walk = (x - this.startX) * 1.5;
      this.moveDistance = Math.abs(walk);
      if (this.moveDistance > 6) {
        this.hasMoved = true;
      }
      vp.scrollLeft = this.scrollLeft - walk;
    };

    const handleMouseUp = () => {
      if (this.isDown) {
        this.isDown = false;
        vp.classList.remove('is-dragging');
        setTimeout(() => {
          this.hasMoved = false;
        }, 50);
      }
    };

    vp.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // Touch Swiping (Mobile / Tablet)
    let touchStartX = 0;
    let isTouchDown = false;

    vp.addEventListener('touchstart', (e) => {
      if (e.target.closest('button, input, select, textarea, .product-card__atc, .product-card__quickview, .iconic-floating-btn')) {
        return;
      }
      isTouchDown = true;
      this.hasMoved = false;
      touchStartX = e.touches[0].pageX;
    }, { passive: true });

    vp.addEventListener('touchmove', (e) => {
      if (!isTouchDown) return;
      const currentX = e.touches[0].pageX;
      const touchWalk = touchStartX - currentX;
      if (Math.abs(touchWalk) > 8) {
        this.hasMoved = true;
      }
    }, { passive: true });

    vp.addEventListener('touchend', () => {
      isTouchDown = false;
      setTimeout(() => {
        this.hasMoved = false;
      }, 50);
    });

    // Prevent click on links only if the user actually dragged
    vp.addEventListener('click', (e) => {
      if (this.hasMoved) {
        e.preventDefault();
        e.stopPropagation();
        this.hasMoved = false;
      }
    }, true);
  }

  updateArrowStates() {
    const vp = this.viewport;
    const maxScroll = Math.max(0, vp.scrollWidth - vp.clientWidth);
    const tolerance = 8;

    if (this.prevBtn) {
      this.prevBtn.disabled = vp.scrollLeft <= tolerance;
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = vp.scrollLeft >= maxScroll - tolerance || maxScroll === 0;
    }
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.onScroll && this.viewport) {
      this.viewport.removeEventListener('scroll', this.onScroll);
    }
  }
}

// Global initialization function
function initAllIconicSliders() {
  document.querySelectorAll('.iconic-slider-wrapper').forEach((wrapper) => {
    if (wrapper._iconicSliderInstance) {
      wrapper._iconicSliderInstance.destroy();
    }
    wrapper._iconicSliderInstance = new IconicProductSlider(wrapper);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllIconicSliders);
} else {
  initAllIconicSliders();
}

// Shopify Theme Customizer events
['shopify:section:load', 'shopify:section:select', 'shopify:section:reorder'].forEach((evt) => {
  document.addEventListener(evt, () => {
    setTimeout(initAllIconicSliders, 100);
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector ? event.target.querySelector('.iconic-slider-wrapper') : null;
  if (section && section._iconicSliderInstance) {
    section._iconicSliderInstance.destroy();
    delete section._iconicSliderInstance;
  }
});
